// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Upload, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { parseCsv, type RowResult } from '@/lib/parse-csv'
import { usePatients } from '@/context/patient-store-context'

type ImportState = 'idle' | 'preview' | 'success'

function downloadTemplate() {
  const blob = new Blob(['name,dob,appointment_date_time,provider,phone,email\n'], {
    type: 'text/csv',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'patient-import-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export default function BulkImportSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { importPatients } = usePatients()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [importState, setImportState] = useState<ImportState>('idle')
  const [rows, setRows] = useState<RowResult[]>([])
  const [truncated, setTruncated] = useState(false)
  const [importedCount, setImportedCount] = useState(0)

  const validRows = rows.filter(r => r.errors.length === 0)
  const errorCount = rows.length - validRows.length

  function processText(text: string) {
    const result = parseCsv(text)
    setRows(result.rows)
    setTruncated(result.truncated)
    setImportState('preview')
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const text = ev.target?.result
      if (typeof text === 'string') processText(text)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const text = ev.target?.result
      if (typeof text === 'string') processText(text)
    }
    reader.readAsText(file)
  }

  function handleImport() {
    const count = importPatients(validRows.map(r => r.data))
    setImportedCount(count)
    setImportState('success')
  }

  function handleClear() {
    setRows([])
    setTruncated(false)
    setImportState('idle')
  }

  if (importState === 'success') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
          <p className="text-sm font-medium text-foreground">
            {t('settings.bulkImport.successMessage', { count: importedCount })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="accent" size="sm" onClick={() => navigate('/patients')}>
            {t('settings.bulkImport.viewPatients')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear}>
            {t('settings.bulkImport.importAnother')}
          </Button>
        </div>
      </div>
    )
  }

  if (importState === 'preview') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={errorCount === 0 ? 'completed' : 'default'}>
            {t('settings.bulkImport.rowSummary', { valid: validRows.length, errors: errorCount })}
          </Badge>
          {truncated && (
            <Badge variant="default" className="bg-amber-500/15 text-amber-600 border-amber-500/30">
              {t('settings.bulkImport.rowLimitWarning')}
            </Badge>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-border max-h-72 overflow-y-auto">
          <table className="w-full min-w-[500px] text-xs">
            <thead>
              <tr className="bg-secondary/80 sticky top-0">
                {[
                  t('settings.bulkImport.colName'),
                  t('settings.bulkImport.colDob'),
                  t('settings.bulkImport.colProvider'),
                  t('settings.bulkImport.colAppointment'),
                  t('settings.bulkImport.colError'),
                ].map(h => (
                  <th
                    key={h}
                    className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 py-2"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.rowNumber}
                  className={[
                    'border-t border-border',
                    row.errors.length > 0
                      ? 'border-l-2 border-l-destructive bg-destructive/5'
                      : '',
                  ].join(' ')}
                >
                  <td className="px-3 py-2 text-foreground">{row.data.name || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.data.dob || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.data.provider}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {row.data.appointmentDateTime || '—'}
                  </td>
                  <td className="px-3 py-2 text-destructive">{row.errors.join('; ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="accent"
            size="sm"
            onClick={handleImport}
            disabled={validRows.length === 0}
          >
            {t('settings.bulkImport.importButton', { count: validRows.length })}
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear}>
            {t('settings.bulkImport.clearButton')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
        }}
        className="flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors text-center"
      >
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Upload className="h-5 w-5 text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">{t('settings.bulkImport.dropLabel')}</p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        type="button"
        onClick={downloadTemplate}
        className="text-xs text-primary hover:text-primary/80 transition-colors font-medium self-start"
      >
        {t('settings.bulkImport.downloadTemplate')}
      </button>
    </div>
  )
}
